import React, { useState, useEffect, useRef } from "react";
import DataNotFound from "components/particles/table/data-not-found";
import attendeAvatar from "assets/images/common/others/attendeAvatar.png";
import useStore from "hooks/useStore";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import moment from "moment";
import { USER_APIS } from "libs/apis/users.api";
import { NotificationIcon } from "assets/images/common/svgs";

interface Message {
  id: number;
  message: string;
  messageType: "TEXT" | "LINK";
  createdAt: string;
  userName: string;
  userProfilePic: string | null;
  sentByMe: boolean;
  status?: "sending" | "sent" | "delivered" | "failed";
}

const AmbassadorCommunity = ({ eventId }: { eventId: number }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [lastSentMessage, setLastSentMessage] = useState<string>("");
  const [lastSentTime, setLastSentTime] = useState<number>(0);
  const sentMessagesRef = useRef<Set<string>>(new Set());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineToast, setShowOfflineToast] = useState(false);
  const offlineToastShownRef = useRef(false);
  const networkErrorCountRef = useRef(0);

  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const isLoadingRef = useRef(false);

  const { getUser } = useStore();
  const userData = getUser();
  const userProfilePic = userData?.profilePic?.name
    ? concatPathWithBackendUrl(userData.profilePic.name)
    : attendeAvatar;
  const myUserId = userData?.id;

  const formatMessage = (msg: any): Message => ({
    id: msg.id,
    message: msg.message,
    messageType: msg.message_type,
    createdAt: msg.createdAt,
    userName: msg.user 
      ? `${msg.user.firstName || ''} ${msg.user.lastName || ''}`.trim() || "User"
      : "Admin",
    userProfilePic: msg.user?.profilePic?.name
      ? concatPathWithBackendUrl(msg.user.profilePic.name)
      : null,
    sentByMe: msg.user_id === myUserId,
    status: "delivered",
  });

  const formatTime = (dateStr: string): string => {
    const now = moment();
    const messageTime = moment(dateStr);
    const diffInMinutes = now.diff(messageTime, 'minutes');
    
    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    return messageTime.format("hh:mm A");
  };

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      offlineToastShownRef.current = false;
      networkErrorCountRef.current = 0;
      setShowOfflineToast(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (!offlineToastShownRef.current) {
        offlineToastShownRef.current = true;
        setShowOfflineToast(true);
        setTimeout(() => setShowOfflineToast(false), 3000);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const fetchInitialMessages = async () => {
    if (!eventId || isLoadingRef.current || !isOnline) return;
    
    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);
    setCurrentPage(0);
    setHasMoreMessages(true);
    
    try {
      const response = await USER_APIS.getAmbassadorCommunityMessages(eventId, {
        page: 0,
        size: 20,
      });
      
      if (response?.status && response.data?.content) {
        const newMsgs = response.data.content.map(formatMessage).reverse();
        setMessages(newMsgs);
        setHasMoreMessages(!response.data.last && response.data.content.length === 20);
        setCurrentPage(0);
        networkErrorCountRef.current = 0;
      } else {
        setMessages([]);
        setHasMoreMessages(false);
      }
    } catch (error) {
      networkErrorCountRef.current++;
      if (networkErrorCountRef.current === 1) {
        setError("Network error! Please check your internet connection.");
        if (!offlineToastShownRef.current) {
          offlineToastShownRef.current = true;
          setShowOfflineToast(true);
          setTimeout(() => setShowOfflineToast(false), 3000);
        }
      }
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };

  const fetchOlderMessages = async () => {
    if (!eventId || isLoadingRef.current || !hasMoreMessages) return;
    
    isLoadingRef.current = true;
    setIsLoadingMore(true);
    setError(null);
    
    try {
      const nextPage = currentPage + 1;
      
      if (chatRef.current) {
        scrollPositionRef.current = chatRef.current.scrollHeight;
      }
      
      const response = await USER_APIS.getAmbassadorCommunityMessages(eventId, {
        page: nextPage,
        size: 20,
      });
      
      if (response?.status && response.data?.content && response.data.content.length > 0) {
        const newMsgs = response.data.content.map(formatMessage).reverse();
        
        setMessages(prev => {
          const existingMessages = new Set(prev.map(m => `${m.message}-${m.createdAt}-${m.sentByMe}`));
          const filtered = newMsgs.filter(m => 
            !existingMessages.has(`${m.message}-${m.createdAt}-${m.sentByMe}`)
          );
          
          if (filtered.length > 0) {
            return [...filtered, ...prev];
          }
          return prev;
        });
        
        setHasMoreMessages(!response.data.last && response.data.content.length === 20);
        setCurrentPage(nextPage);
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      setError("Failed to load older messages");
    } finally {
      setIsLoadingMore(false);
      isLoadingRef.current = false;
    }
  };

  const pollForNewMessages = async () => {
    if (!eventId || isLoadingRef.current || isSending || !isOnline) return;
    
    try {
      const response = await USER_APIS.getAmbassadorCommunityMessages(eventId, {
        page: 0,
        size: 10,
      });
      
      if (response?.status && response.data?.content) {
        const newMsgs = response.data.content.map(formatMessage);
        
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const filtered = newMsgs.filter(m => !existingIds.has(m.id));
          
          if (filtered.length > 0) {
            const updated = [...prev, ...filtered.reverse()];
            return updated.filter((message, index, arr) => 
              arr.findIndex(m => m.id === message.id) === index
            );
          }
          return prev;
        });
        networkErrorCountRef.current = 0;
      }
    } catch (error) {
      networkErrorCountRef.current++;
      if (networkErrorCountRef.current === 1) {
        if (!offlineToastShownRef.current) {
          offlineToastShownRef.current = true;
          setShowOfflineToast(true);
          setTimeout(() => setShowOfflineToast(false), 3000);
        }
      }
    }
  };

  const generateUniqueId = () => {
    return Date.now() * 1000 + Math.floor(Math.random() * 1000);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !eventId || isSending || !isOnline) return;

    const message = newMessage.trim();
    const currentTime = Date.now();
    const messageKey = `${message}-${currentTime}`;
    
    if (sentMessagesRef.current.has(messageKey)) {
      return;
    }

    if (message === lastSentMessage && (currentTime - lastSentTime) < 5000) {
      return;
    }

    const messageExists = messages.some(m => 
      m.message === message && 
      m.sentByMe === true && 
      (currentTime - new Date(m.createdAt).getTime()) < 5000
    );
    
    if (messageExists) {
      return;
    }

    sentMessagesRef.current.add(messageKey);
    const messageType = message.startsWith("http") ? "LINK" : "TEXT";
    setNewMessage("");
    setIsSending(true);
    setLastSentMessage(message);
    setLastSentTime(currentTime);

    const msg: Message = {
      id: generateUniqueId(),
      message,
      messageType,
      createdAt: new Date().toISOString(),
      userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : "You",
      userProfilePic: userProfilePic,
      sentByMe: true,
      status: "sending",
    };

    setMessages(prev => {
      const exists = prev.some(m => 
        m.message === msg.message && 
        m.sentByMe === msg.sentByMe && 
        Math.abs(new Date(m.createdAt).getTime() - new Date(msg.createdAt).getTime()) < 2000
      );
      if (exists) return prev;
      return [...prev, msg];
    });

    try {
      const response = await USER_APIS.postAmbassadorCommunityMessages(eventId, {
        message,
        message_type: messageType,
      });

      if (response?.status) {
        setMessages(prev => 
          prev.map(m => m.id === msg.id ? { ...m, status: "sent" } : m)
        );
        
        setTimeout(() => {
          setMessages(prev => 
            prev.map(m => m.id === msg.id ? { ...m, status: "delivered" } : m)
          );
        }, 1000);
        networkErrorCountRef.current = 0;
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setMessages(prev => 
        prev.map(m => m.id === msg.id ? { ...m, status: "failed" } : m)
      );
      if (!offlineToastShownRef.current) {
        offlineToastShownRef.current = true;
        setShowOfflineToast(true);
        setTimeout(() => setShowOfflineToast(false), 3000);
      }
    } finally {
      setIsSending(false);
    }
    
    setTimeout(() => {
      setLastSentMessage("");
      setLastSentTime(0);
      sentMessagesRef.current.delete(messageKey);
    }, 5000);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const retryFailedMessage = async (message: Message) => {
    if (!eventId || isSending || !isOnline) return;

    setIsSending(true);
    
    setMessages(prev => 
      prev.map(m => m.id === message.id ? { ...m, status: "sending" } : m)
    );

    try {
      const response = await USER_APIS.postAmbassadorCommunityMessages(eventId, {
        message: message.message,
        message_type: message.messageType,
      });

      if (response?.status) {
        setMessages(prev => 
          prev.map(m => m.id === message.id ? { ...m, status: "sent" } : m)
        );
        
        setTimeout(() => {
          setMessages(prev => 
            prev.map(m => m.id === message.id ? { ...m, status: "delivered" } : m)
          );
        }, 1000);
        networkErrorCountRef.current = 0;
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setMessages(prev => 
        prev.map(m => m.id === message.id ? { ...m, status: "failed" } : m)
      );
      if (!offlineToastShownRef.current) {
        offlineToastShownRef.current = true;
        setShowOfflineToast(true);
        setTimeout(() => setShowOfflineToast(false), 3000);
      }
    } finally {
      setIsSending(false);
    }
  };

  const maintainScrollPosition = () => {
    if (chatRef.current && scrollPositionRef.current > 0) {
      const newScrollHeight = chatRef.current.scrollHeight;
      const scrollDifference = newScrollHeight - scrollPositionRef.current;
      chatRef.current.scrollTop = scrollDifference;
      scrollPositionRef.current = 0;
    }
  };

  const handleScroll = () => {
    if (!chatRef.current || isLoadingRef.current || !hasMoreMessages) return;

    const { scrollTop } = chatRef.current;
    const threshold = 100;

    if (scrollTop < threshold) {
      fetchOlderMessages();
    }
  };

  useEffect(() => {
    if (chatRef.current && !isLoadingMore) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages.length, isLoadingMore]);

  useEffect(() => {
    if (isLoadingMore === false && scrollPositionRef.current > 0) {
      requestAnimationFrame(() => {
        maintainScrollPosition();
      });
    }
  }, [isLoadingMore]);

  useEffect(() => {
    if (eventId && isOnline) {
      fetchInitialMessages();
      intervalRef.current = setInterval(pollForNewMessages, 10000);
    } else if (!isOnline) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [eventId, isOnline]);

  return (
    <div className="flex flex-col h-screen relative bg-white">
      {showOfflineToast && (
        <div className="bg-red-500 text-white px-4 py-2 text-center text-sm font-medium">
          Network error! Please check your internet connection.
        </div>
      )}
      {!isOnline && (
        <div className="bg-orange-500 text-white px-4 py-2 text-center text-sm font-medium">
          You're offline. Messages will be sent when connection is restored.
        </div>
      )}
      <div 
        className="flex-1 overflow-y-auto py-4 px-3 scroll-smooth" 
        ref={chatRef}
        onScroll={handleScroll}
      >

        {!isLoading && messages.length === 0 && (
          <DataNotFound show={true} />
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 mx-2">
            {error}
          </div>
        )}

        {messages.length > 0 && (
          <>
            {messages
              .filter((message, index, arr) => {
                return arr.findIndex(m => m.id === message.id) === index;
              })
              .filter((message, index, arr) => {
                return arr.findIndex(m => 
                  m.message === message.message && 
                  m.sentByMe === message.sentByMe && 
                  Math.abs(new Date(m.createdAt).getTime() - new Date(message.createdAt).getTime()) < 1000
                ) === index;
              })
              .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
              .map((message, index) => (
              <div
                key={`${message.id}-${message.createdAt}-${message.sentByMe}`}
                className={`flex items-end mb-4 px-4 ${
                  message.sentByMe ? "justify-end" : "justify-start"
                }`}
              >
                {!message.sentByMe && (
                  <div className="w-8 h-8 rounded-full mr-2 flex-shrink-0 overflow-hidden">
                    {message.userProfilePic ? (
                      <img 
                        src={message.userProfilePic} 
                        alt={message.userName || 'User'} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.className = 'w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center text-white text-xs font-semibold mr-2 flex-shrink-0';
                            parent.textContent = message.userName ? message.userName.split(' ').map(n => n[0]).join('').toUpperCase() : 'AU';
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-blue-400 flex items-center justify-center text-white text-xs font-semibold">
                        {message.userName ? message.userName.split(' ').map(n => n[0]).join('').toUpperCase() : 'AU'}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-col max-w-xs">
                  {!message.sentByMe && (
                    <p className="text-xs text-gray-600 mb-1 px-1">
                      {message.userName}
                    </p>
                  )}
                  <div
                    className={`px-3 py-2 rounded-xl ${
                      message.sentByMe
                        ? "bg-blue-400 text-black rounded-br-sm"
                        : "bg-gray-200 text-black rounded-bl-sm"
                    }`}
                  >
                    <p className="break-words text-sm">
                      {message.messageType === "LINK" ? (
                        <a
                          href={message.message.startsWith("http") ? message.message : `https://${message.message}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:opacity-80 text-blue-600"
                        >
                          {message.message}
                        </a>
                      ) : (
                        message.message
                      )}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-1 px-1">
                    <p className="text-xs text-gray-500">
                      {formatTime(message.createdAt)}
                    </p>
                    {message.sentByMe && (
                      <div className="flex items-center ml-2">
                        {message.status === "sending" && (
                          <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>
                        )}
                        {message.status === "sent" && (
                          <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {message.status === "delivered" && (
                          <div className="flex items-center">
                            <svg className="w-3 h-3 text-gray-400 -ml-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        {message.status === "failed" && (
                          <button
                            onClick={() => {
                              retryFailedMessage(message);
                            }}
                            className="w-3 h-3 text-red-500 hover:text-red-700 transition-colors"
                            title="Retry sending message"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
            
                </div>
                {message.sentByMe && (
                  <div className="w-8 h-8 rounded-full ml-2 flex-shrink-0 overflow-hidden">
                    {userProfilePic ? (
                      <img 
                        src={userProfilePic} 
                        alt={userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : 'You'} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.className = 'w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center text-white text-xs font-semibold ml-2 flex-shrink-0';
                            parent.textContent = userData?.firstName ? userData.firstName[0].toUpperCase() : 'U';
                            if (userData?.lastName) {
                              parent.textContent += userData.lastName[0].toUpperCase();
                            }
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-blue-400 flex items-center justify-center text-white text-xs font-semibold">
                        {userData?.firstName ? userData.firstName[0].toUpperCase() : 'U'}
                        {userData?.lastName ? userData.lastName[0].toUpperCase() : ''}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      <div className="border-t border-gray-200 bg-white px-4 py-3">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
        >
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className="w-full h-10 px-4 pr-12 border border-gray-300 rounded-full outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all duration-200 bg-white"
              maxLength={1000}
              disabled={!eventId}
              aria-label="Message input"
            />
          </div>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white p-2 rounded-full transition-all duration-200 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
            disabled={!newMessage.trim() || !eventId || isSending}
            aria-label="Send message"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default AmbassadorCommunity;